import { useState, useEffect, useCallback } from 'react';

export interface CallerIdDevice {
  port: string | null;
  connected: boolean;
  model: string | null;
}

export interface IncomingCall {
  phoneNumber: string;
  name?: string;
  date?: string;
  time?: string;
  line?: string;
  timestamp: Date;
}

export interface CallerIdStatus {
  supported: boolean;
  connected: boolean;
  port: string | null;
  lastCall: IncomingCall | null;
  error: string | null;
  deviceModel: string | null;
}

// CIDShow uses 1200 baud, 8N1 standard Caller ID format
const CALLER_ID_BAUD_RATE = 1200;

export function useCallerId() {
  const [status, setStatus] = useState<CallerIdStatus>({
    supported: false,
    connected: false,
    port: null,
    lastCall: null,
    error: null,
    deviceModel: null,
  });
  const [port, setPort] = useState<SerialPort | null>(null);

  // Check if Web Serial API is supported
  const checkSupport = useCallback(() => {
    const supported = 'serial' in navigator;
    setStatus(prev => ({ ...prev, supported }));
    return supported;
  }, []);

  // Get available ports
  const getPorts = useCallback(async (): Promise<SerialPort[]> => {
    try {
      const ports = await (navigator as any).serial.getPorts();
      return ports;
    } catch (error) {
      console.error('Failed to get ports:', error);
      return [];
    }
  }, []);

  // Parse CIDShow Caller ID data format
  // Format: CID:DATE*DDMMYY*TIME*HHMM*LINE*LINEID*NMBR*PHONENUMBER*NAME*NAME
  // Or: NMBR=phonenumber, DATE=DDMMYY
  const parseCallerIdData = (data: string): IncomingCall | null => {
    console.log('📞 Raw data received:', data);
    
    try {
      // Try CID:DATE format (CIDShow standard)
      // Example: CID:*DATE*180426*TIME*1425*LINE*POTS*NMBR*5321234567*NAME*John Doe
      const cidMatch = data.match(/CID:.*?NMBR\*([^*]+)\*NAME\*([^*]+)/i);
      if (cidMatch) {
        const phoneNumber = cidMatch[1]?.trim() || '';
        const name = cidMatch[2]?.trim() || '';
        
        if (phoneNumber && phoneNumber.length >= 7) {
          console.log('📞 Parsed call:', { phoneNumber, name });
          return {
            phoneNumber,
            name: name || undefined,
            timestamp: new Date(),
          };
        }
      }
      
      // Try NMBR= format
      const nmbrMatch = data.match(/NMBR=[\s]*(\d+)/i);
      if (nmbrMatch) {
        const dateMatch = data.match(/DATE=[\s]*(\d{6})/i);
        const timeMatch = data.match(/TIME=[\s]*(\d{4})/i);
        
        return {
          phoneNumber: nmbrMatch[1],
          date: dateMatch?.[1],
          time: timeMatch?.[1],
          timestamp: new Date(),
        };
      }
      
      // Try simple RING + number pattern
      const ringMatch = data.match(/RING[\s\S]*?(\d{10,11})/i);
      if (ringMatch) {
        return {
          phoneNumber: ringMatch[1],
          timestamp: new Date(),
        };
      }
      
      // Try LINE pattern: LINE*0*NMBR*phonenumber*NAME*name
      const lineMatch = data.match(/LINE\*\d+\*NMBR\*(\d+)\*NAME\*([^*]+)/i);
      if (lineMatch) {
        return {
          phoneNumber: lineMatch[1],
          name: lineMatch[2]?.trim() || undefined,
          timestamp: new Date(),
        };
      }
      
    } catch (error) {
      console.error('📞 Parse error:', error);
    }
    
    return null;
  };

  // Read data from serial port
  const readFromPort = useCallback(async (serialPort: SerialPort, onCall: (call: IncomingCall) => void) => {
    const reader = serialPort.readable.getReader();
    let buffer = '';
    
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        // Convert to string
        const decoder = new TextDecoder();
        const text = decoder.decode(value);
        buffer += text;
        
        // Look for complete caller ID data (ends with newline or specific patterns)
        if (buffer.includes('CID:') || buffer.includes('NMBR=') || buffer.includes('RING')) {
          const parsedCall = parseCallerIdData(buffer);
          if (parsedCall) {
            setStatus(prev => ({
              ...prev,
              lastCall: parsedCall,
            }));
            onCall(parsedCall);
            // Clear buffer after successful parse
            buffer = '';
          }
        }
        
        // Keep only last 1000 chars to avoid memory issues
        if (buffer.length > 1000) {
          buffer = buffer.slice(-500);
        }
      }
    } catch (error) {
      console.error('📞 Read error:', error);
      setStatus(prev => ({
        ...prev,
        error: String(error),
      }));
    } finally {
      reader.releaseLock();
    }
  }, []);

  // Connect to caller ID device
  const connect = useCallback(async (): Promise<boolean> => {
    try {
      // Request port from user
      const selectedPort = await (navigator as any).serial.requestPort();
      
      await selectedPort.open({
        baudRate: CALLER_ID_BAUD_RATE,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
      });

      setPort(selectedPort);
      setStatus(prev => ({
        ...prev,
        connected: true,
        port: selectedPort.portId || 'Unknown',
        deviceModel: 'CIDShow (USB)',
        error: null,
      }));

      // Start reading data with callback
      readFromPort(selectedPort, (call) => {
        console.log('📞 Incoming call detected:', call);
      });
      
      console.log('📞 Caller ID device connected');
      return true;
    } catch (error: any) {
      console.error('📞 Caller ID connection failed:', error);
      setStatus(prev => ({
        ...prev,
        connected: false,
        error: error.message || 'Connection failed',
      }));
      return false;
    }
  }, [readFromPort]);

  // Disconnect from device
  const disconnect = useCallback(async () => {
    if (port) {
      try {
        await port.close();
        setPort(null);
        setStatus(prev => ({
          ...prev,
          connected: false,
          port: null,
          deviceModel: null,
        }));
        console.log('📞 Caller ID device disconnected');
        return true;
      } catch (error) {
        console.error('Disconnect failed:', error);
        return false;
      }
    }
    return false;
  }, [port]);

  // Check support on mount
  useEffect(() => {
    checkSupport();
  }, [checkSupport]);

  return {
    status,
    connect,
    disconnect,
    getPorts,
    checkSupport,
  };
}

// Hook for customer lookup based on caller ID
export function useCallerIdWithCustomerLookup(onIncomingCall: (call: IncomingCall, customer?: any) => void) {
  const callerId = useCallerId();

  // When a call comes in, lookup customer
  useEffect(() => {
    if (callerId.status.lastCall) {
      const phone = callerId.status.lastCall.phoneNumber;
      console.log('📞 Looking up customer for:', phone);
      // The actual customer lookup would be done by the component using this hook
      onIncomingCall(callerId.status.lastCall);
    }
  }, [callerId.status.lastCall, onIncomingCall]);

  return callerId;
}

// Check if Caller ID is supported
export function isCallerIdSupported(): boolean {
  return 'serial' in navigator;
}